<template>
  <div class="marketing-stock-admin-root pa-0 pa-md-4 fill-height align-start d-flex flex-column" dark>
    <!-- ============ HEADER BANNER ============ -->
    <v-card class="mb-4 w-100 rounded-xl header-banner" flat dark>
      <div class="header-banner-glow"></div>
      <div class="pa-5 d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center position-relative" style="z-index: 2">
        <div>
          <div class="caption text-uppercase font-weight-bold letter-spaced muted-text mb-1">
            <v-icon x-small color="primary" class="mr-1">mdi-shield-crown-outline</v-icon>
            Marketing-Lager Administration
          </div>
          <div class="display-1 font-weight-black white--text header-title">Lager verwalten</div>
        </div>
        <v-btn to="/dashboard/communication/stock" color="primary" rounded outlined class="mt-3 mt-sm-0 products-nav-btn">
          <v-icon left>mdi-arrow-left</v-icon>
          Zur Produkt-Ansicht
        </v-btn>
      </div>
    </v-card>

    <!-- ============ TABS NAVIGATION ============ -->
    <v-tabs v-model="activeTab" background-color="transparent" dark color="primary" class="mb-4 tabs-nav">
      <v-tab class="text-none font-weight-bold"><v-icon left>mdi-package-variant</v-icon>Produkte verwalten</v-tab>
      <v-tab class="text-none font-weight-bold"><v-icon left>mdi-history</v-icon>Protokoll / Historie</v-tab>
    </v-tabs>

    <!-- ============ TAB ITEMS ============ -->
    <v-tabs-items v-model="activeTab" class="transparent w-100 flex-grow-1 d-flex flex-column">
      <!-- TAB 1: PRODUCT MANAGEMENT -->
      <v-tab-item>
        <div class="fill-height d-flex flex-column">
          <v-card flat class="rounded-xl pa-4 table-card" dark>
            <div class="d-flex align-center justify-space-between mb-4">
              <v-text-field
                v-model="productSearch"
                placeholder="Produkte durchsuchen..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                rounded
                background-color="rgba(255,255,255,0.04)"
                style="max-width: 320px"
                class="search-field"
                color="primary"
                clearable></v-text-field>

              <v-btn color="primary" rounded depressed @click="openCreateProductDialog" class="font-weight-bold">
                <v-icon left>mdi-plus</v-icon>
                Neues Produkt
              </v-btn>
            </div>

            <v-data-table
              :headers="productHeaders"
              :items="products"
              :search="productSearch"
              :loading="loadingProducts"
              :items-per-page="15"
              dark
              class="transparent-table"
              no-data-text="Keine Produkte erfasst"
              no-results-text="Keine passenden Produkte gefunden"
              loading-text="Produkte werden geladen...">
              <!-- Barcode Custom Rendering -->
              <template v-slot:item.barcode="{ item }">
                <span class="font-mono caption grey--text"><v-icon x-small color="grey" class="mr-1">mdi-barcode</v-icon>{{ item.barcode }}</span>
              </template>

              <!-- Stock Custom Rendering -->
              <template v-slot:item.stock="{ item }">
                <v-chip v-if="item.stock !== null" :color="getStockColor(item.stock)" x-small dark class="font-weight-bold">
                  {{ item.stock }}
                </v-chip>
                <span v-else class="caption grey--text text-italic">Unbegrenzt (0)</span>
              </template>

              <!-- Notification Custom Rendering -->
              <template v-slot:item.notification="{ item }">
                <div v-if="item.notificationThreshold !== null" class="caption">
                  <v-icon x-small color="warning" class="mr-1">mdi-bell-outline</v-icon>
                  <span>Bei &lt; {{ item.notificationThreshold }}</span>
                  <span class="d-block text-xs grey--text mt-1">{{ item.notificationEmail }}</span>
                </div>
                <span v-else class="caption grey--text">–</span>
              </template>

              <!-- Active Custom Rendering -->
              <template v-slot:item.active="{ item }">
                <v-chip :color="item.active ? 'success' : 'grey'" x-small dark class="font-weight-bold">
                  {{ item.active ? "Aktiv" : "Inaktiv" }}
                </v-chip>
              </template>

              <!-- Actions Custom Rendering -->
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="primary" @click="openEditProductDialog(item)" class="mr-1">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="confirmDeleteProduct(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </v-tab-item>

      <!-- TAB 2: AUDIT LOG -->
      <v-tab-item>
        <div class="fill-height d-flex flex-column">
          <v-card flat class="rounded-xl pa-4 table-card" dark>
            <!-- Filters Row -->
            <v-row class="mb-4 align-center ma-0 pa-2 rounded-lg filter-bar" style="background-color: rgba(255, 255, 255, 0.02); gap: 12px">
              <!-- Filter: Person -->
              <v-col cols="12" sm="6" md="3" class="pa-1">
                <v-select
                  v-model="filters.createdBy"
                  :items="personFilterOptions"
                  label="Person"
                  dense
                  outlined
                  hide-details
                  rounded
                  background-color="rgba(255,255,255,0.03)"
                  color="primary"></v-select>
              </v-col>

              <!-- Filter: Category -->
              <v-col cols="12" sm="6" md="2" class="pa-1">
                <v-select
                  v-model="filters.category"
                  :items="categoryFilterOptions"
                  label="Kategorie"
                  dense
                  outlined
                  hide-details
                  rounded
                  background-color="rgba(255,255,255,0.03)"
                  color="primary"></v-select>
              </v-col>

              <!-- Filter: Product -->
              <v-col cols="12" sm="6" md="3" class="pa-1">
                <v-select
                  v-model="filters.product"
                  :items="productFilterOptions"
                  label="Produkt"
                  dense
                  outlined
                  hide-details
                  rounded
                  background-color="rgba(255,255,255,0.03)"
                  color="primary"></v-select>
              </v-col>

              <!-- Filter: Studio -->
              <v-col cols="12" sm="6" md="3" class="pa-1">
                <v-select
                  v-model="filters.studio"
                  :items="studioFilterOptions"
                  label="Studio"
                  dense
                  outlined
                  hide-details
                  rounded
                  background-color="rgba(255,255,255,0.03)"
                  color="primary"></v-select>
              </v-col>
            </v-row>

            <v-data-table
              :headers="orderHeaders"
              :items="filteredOrders"
              :loading="loadingOrders"
              :items-per-page="20"
              dark
              class="transparent-table"
              no-data-text="Keine Buchungen vorhanden"
              loading-text="Buchungsverlauf wird geladen...">
              <!-- Date Rendering -->
              <template v-slot:item.createdAt="{ item }">
                <span class="caption">{{ formatDateTime(item.createdAt) }}</span>
              </template>

              <!-- User Rendering -->
              <template v-slot:item.createdBy="{ item }">
                <span class="font-weight-medium" v-if="item.createdBy"> {{ item.createdBy.vorname }} {{ item.createdBy.nachname }} </span>
                <span class="grey--text caption" v-else>Unbekannt</span>
              </template>

              <!-- Product Title/Barcode -->
              <template v-slot:item.productTitle="{ item }">
                <div>
                  <span class="font-weight-medium white--text d-block">{{ item.productTitle }}</span>
                  <span class="font-mono text-xs grey--text"
                    ><v-icon x-small color="grey" class="mr-1">mdi-barcode</v-icon>{{ item.productBarcode }}</span
                  >
                </div>
              </template>

              <!-- Studio Rendering -->
              <template v-slot:item.studio="{ item }">
                <span v-if="item.studio">{{ item.studio.name }}</span>
                <span class="grey--text caption" v-else>–</span>
              </template>

              <!-- Quantity Custom Rendering -->
              <template v-slot:item.quantity="{ item }">
                <v-chip color="rgba(25, 118, 210, 0.15)" text-color="primary" small class="font-weight-bold">
                  {{ item.quantity }}
                </v-chip>
              </template>

              <!-- Comment Rendering -->
              <template v-slot:item.comment="{ item }">
                <span v-if="item.comment" class="caption text-italic grey--text text--lighten-1" :title="item.comment">
                  {{ truncateText(item.comment, 40) }}
                </span>
                <span class="grey--text caption" v-else>–</span>
              </template>

              <!-- Actions Rendering -->
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="primary" @click="openEditOrderDialog(item)" class="mr-1" title="Menge bearbeiten">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="confirmCancelOrder(item)" title="Buchung stornieren">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- ============ CREATE/EDIT PRODUCT DIALOG ============ -->
    <v-dialog v-model="productDialogOpen" max-width="600" persistent class="product-form-dialog">
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5">
          <v-icon left color="primary">{{ isEditMode ? "mdi-pencil" : "mdi-plus-circle" }}</v-icon>
          {{ isEditMode ? "Produkt bearbeiten" : "Neues Produkt erfassen" }}
        </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2">
          <v-form ref="productForm" v-model="productFormValid">
            <v-row>
              <!-- Title -->
              <v-col cols="12" class="py-1">
                <v-text-field
                  v-model="productFormModel.title"
                  label="Produkt-Titel *"
                  outlined
                  rounded
                  color="primary"
                  :rules="[(v) => !!v || 'Titel ist erforderlich']"></v-text-field>
              </v-col>

              <!-- Barcode -->
              <v-col cols="12" class="py-1">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="productFormModel.barcode"
                    label="Barcode *"
                    outlined
                    rounded
                    color="primary"
                    :rules="[(v) => !!v || 'Barcode ist erforderlich']"
                    append-icon="mdi-refresh"
                    @click:append="generateRandomBarcode"
                    messages="Klicke auf das Symbol, um einen Barcode zu generieren"
                    class="flex-grow-1"></v-text-field>

                  <v-btn
                    color="primary"
                    fab
                    small
                    depressed
                    class="ml-2 mb-6"
                    title="Barcode fotografieren/scannen"
                    :loading="scanningFile"
                    @click="triggerAdminCaptureFlow">
                    <v-icon>mdi-camera</v-icon>
                  </v-btn>
                </div>

                <!-- Hidden Admin File Input for barcode scanning -->
                <input type="file" ref="adminFileInput" accept="image/*" style="display: none" @change="onAdminFileSelected" />
              </v-col>

              <!-- Category -->
              <v-col cols="12" class="py-1">
                <v-combobox
                  v-model="productFormModel.category"
                  :items="categories"
                  label="Kategorie"
                  outlined
                  rounded
                  color="primary"
                  clearable
                  hint="Wähle eine bestehende Kategorie oder tippe eine neue ein"
                  persistent-hint
                  class="mb-3"></v-combobox>
              </v-col>

              <!-- Stock Tracking Option -->
              <v-col cols="12" class="py-1">
                <v-checkbox v-model="trackStock" label="Lagerbestand überwachen" color="primary" hide-details class="mt-0 mb-3"></v-checkbox>
              </v-col>

              <!-- Stock (Visible only if trackStock is true) -->
              <v-col cols="12" class="py-1" v-if="trackStock">
                <v-text-field
                  v-model.number="productFormModel.stock"
                  type="number"
                  label="Aktueller Lagerbestand"
                  outlined
                  rounded
                  color="primary"
                  :rules="[
                    (v) => (v !== undefined && v !== '' && v !== null) || 'Bitte Lagerbestand eingeben',
                    (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
                  ]"></v-text-field>
              </v-col>

              <!-- Notification Option (Visible only if trackStock is true) -->
              <v-col cols="12" class="py-1" v-if="trackStock">
                <v-checkbox
                  v-model="enableNotification"
                  label="E-Mail-Benachrichtigung bei Mindestbestand"
                  color="primary"
                  hide-details
                  class="mt-0 mb-3"></v-checkbox>
              </v-col>

              <!-- Notification Limit (Visible only if enableNotification is true) -->
              <v-col cols="12" sm="6" class="py-1" v-if="trackStock && enableNotification">
                <v-text-field
                  v-model.number="productFormModel.notificationThreshold"
                  type="number"
                  label="Mindestbestand"
                  outlined
                  rounded
                  color="primary"
                  :rules="[(v) => (v !== undefined && v !== '' && v !== null) || 'Bitte Grenzwert eingeben']"></v-text-field>
              </v-col>

              <!-- Notification Email (Visible only if enableNotification is true) -->
              <v-col cols="12" sm="6" class="py-1" v-if="trackStock && enableNotification">
                <v-text-field
                  v-model="productFormModel.notificationEmail"
                  label="Benachrichtigungs-Mail"
                  outlined
                  rounded
                  color="primary"
                  :rules="[
                    (v) => !!v || 'Bitte E-Mail-Adresse eingeben',
                    (v) => /.+@.+\..+/.test(v) || 'Bitte gültige E-Mail-Adresse eingeben',
                  ]"></v-text-field>
              </v-col>

              <!-- Active Status -->
              <v-col cols="12" class="py-1">
                <v-checkbox
                  v-model="productFormModel.active"
                  label="Produkt aktiv (in Produkt-Auswahl anzeigen)"
                  color="primary"
                  hide-details
                  class="mt-0"></v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeProductDialog" class="px-5">Abbrechen</v-btn>
          <v-btn color="primary" rounded depressed class="px-6" :loading="savingProduct" :disabled="!productFormValid" @click="saveProduct">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ DELETE PRODUCT DIALOG ============ -->
    <v-dialog v-model="deleteDialogOpen" max-width="400">
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5"> Produkt deaktivieren? </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2" v-if="productToDelete">
          Möchtest du das Produkt <b>"{{ productToDelete.title }}"</b> wirklich deaktivieren? Es wird nicht mehr in der Produkt-Auswahl für Entnahmen
          angezeigt, bleibt aber im Buchungsverlauf erhalten.
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeDeleteDialog" class="px-5">Abbrechen</v-btn>
          <v-btn color="error" rounded depressed class="px-6" :loading="deletingProduct" @click="executeDeleteProduct"> Deaktivieren </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ EDIT ORDER DIALOG (ADMIN) ============ -->
    <v-dialog v-model="editOrderDialogOpen" max-width="400" persistent>
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5"> Buchung bearbeiten </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2" v-if="orderToEdit">
          <div class="mb-4 text-subtitle-2 grey--text">
            Produkt: <b>{{ orderToEdit.productTitle }}</b
            ><br />
            Studio: <b>{{ orderToEdit.studio ? orderToEdit.studio.name : "–" }}</b>
          </div>

          <v-text-field
            v-model.number="orderFormModel.quantity"
            type="number"
            label="Menge *"
            outlined
            rounded
            min="1"
            color="primary"
            :rules="[
              (v) => !!v || 'Bitte Menge eingeben',
              (v) => Number(v) > 0 || 'Menge muss grösser als 0 sein',
              (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
            ]"></v-text-field>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeEditOrderDialog" class="px-5">Abbrechen</v-btn>
          <v-btn
            color="primary"
            rounded
            depressed
            class="px-6"
            :loading="savingOrderEdit"
            :disabled="!orderFormModel.quantity || orderFormModel.quantity <= 0 || !Number.isInteger(Number(orderFormModel.quantity))"
            @click="saveOrderEdit">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ CANCEL ORDER DIALOG (ADMIN) ============ -->
    <v-dialog v-model="cancelOrderDialogOpen" max-width="400" persistent>
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5"> Buchung stornieren? </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2" v-if="orderToCancel">
          Möchtest du die Buchung von <b>{{ orderToCancel.quantity }}x "{{ orderToCancel.productTitle }}"</b> für das Studio
          <b>{{ orderToCancel.studio ? orderToCancel.studio.name : "–" }}</b> wirklich stornieren?<br />
          Der bezogene Lagerbestand wird dem Produkt wieder gutgeschrieben.
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeCancelOrderDialog" class="px-5">Abbrechen</v-btn>
          <v-btn color="error" rounded depressed class="px-6" :loading="cancelingOrder" @click="executeCancelOrder"> Stornieren </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex"
import api from "@/api"
import moment from "moment"

export default {
  name: "MarketingStockAdmin",
  data() {
    return {
      activeTab: 0,

      // Products Management Tab
      products: [],
      categories: [],
      loadingProducts: false,
      productSearch: "",
      productHeaders: [
        { text: "Barcode", value: "barcode", sortable: true, width: "120px" },
        { text: "Titel", value: "title", sortable: true },
        { text: "Kategorie", value: "category", sortable: true },
        { text: "Lagerbestand", value: "stock", sortable: true, width: "120px" },
        { text: "Mindestbestand-Warnung", value: "notification", sortable: false },
        { text: "Status", value: "active", sortable: true, width: "100px" },
        { text: "Aktionen", value: "actions", sortable: false, width: "100px", align: "end" },
      ],

      // Add/Edit Dialog
      productDialogOpen: false,
      isEditMode: false,
      productFormValid: false,
      savingProduct: false,
      scanningFile: false,
      trackStock: false,
      enableNotification: false,
      productFormModel: {
        _id: null,
        barcode: "",
        title: "",
        category: "Allgemein",
        stock: 0,
        notificationThreshold: 5,
        notificationEmail: "da@wellcomefit.ch",
        active: true,
      },

      // Delete Dialog
      deleteDialogOpen: false,
      productToDelete: null,
      deletingProduct: false,

      // Audit Log Tab
      orders: [],
      loadingOrders: false,
      filters: {
        createdBy: "all",
        category: "all",
        product: "all",
        studio: "all",
      },
      orderHeaders: [
        { text: "Zeitpunkt", value: "createdAt", sortable: true, width: "140px" },
        { text: "Person", value: "createdBy", sortable: true },
        { text: "Produkt", value: "productTitle", sortable: true },
        { text: "Kategorie", value: "productCategory", sortable: true },
        { text: "Studio", value: "studio", sortable: true },
        { text: "Menge", value: "quantity", sortable: true, width: "90px" },
        { text: "Kommentar", value: "comment", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, width: "100px", align: "end" },
      ],
      editOrderDialogOpen: false,
      orderToEdit: null,
      savingOrderEdit: false,
      orderFormModel: { quantity: 1 },
      cancelOrderDialogOpen: false,
      orderToCancel: null,
      cancelingOrder: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    // Dropdown filters content generated dynamically from loaded orders
    personFilterOptions() {
      const options = [{ value: "all", text: "Alle Personen" }]
      const uniquePersons = new Map()

      this.orders.forEach((o) => {
        if (o.createdBy) {
          const id = o.createdBy._id
          const name = `${o.createdBy.vorname} ${o.createdBy.nachname}`
          uniquePersons.set(id, name)
        }
      })

      uniquePersons.forEach((name, id) => {
        options.push({ value: id, text: name })
      })

      return options
    },

    categoryFilterOptions() {
      const options = [{ value: "all", text: "Alle Kategorien" }]
      const uniqueCategories = new Set()

      this.orders.forEach((o) => {
        if (o.productCategory) uniqueCategories.add(o.productCategory)
      })

      uniqueCategories.forEach((cat) => {
        options.push({ value: cat, text: cat })
      })

      return options
    },

    productFilterOptions() {
      const options = [{ value: "all", text: "Alle Produkte" }]
      const uniqueProducts = new Map()

      this.orders.forEach((o) => {
        if (o.product) {
          uniqueProducts.set(o.product._id || o.product, o.productTitle)
        }
      })

      uniqueProducts.forEach((title, id) => {
        options.push({ value: id, text: title })
      })

      return options
    },

    studioFilterOptions() {
      const options = [{ value: "all", text: "Alle Studios" }]

      this.storeStudios
        .filter((s) => s.code && s.code !== "ot" && s.code !== "alibey")
        .forEach((s) => {
          options.push({ value: s._id, text: s.name })
        })

      return options
    },

    filteredOrders() {
      return this.orders.filter((o) => {
        // User filter
        if (this.filters.createdBy !== "all") {
          const creatorId = o.createdBy?._id || o.createdBy
          if (creatorId !== this.filters.createdBy) return false
        }
        // Category filter
        if (this.filters.category !== "all") {
          if (o.productCategory !== this.filters.category) return false
        }
        // Product filter
        if (this.filters.product !== "all") {
          const prodId = o.product?._id || o.product
          if (prodId !== this.filters.product) return false
        }
        // Studio filter
        if (this.filters.studio !== "all") {
          const studId = o.studio?._id || o.studio
          if (studId !== this.filters.studio) return false
        }
        return true
      })
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 0) {
        this.loadProducts()
      } else if (newTab === 1) {
        this.loadOrders()
      }
    },
  },
  mounted() {
    this.loadProducts()
    this.loadCategories()
  },
  methods: {
    // --- LOAD DATA ---
    async loadProducts() {
      this.loadingProducts = true
      try {
        this.products = await api.marketingStock.fetchProducts({ all: true })
      } catch (err) {
        this.$toast.error("Produkte konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingProducts = false
      }
    },

    async loadCategories() {
      try {
        this.categories = await api.marketingStock.fetchCategories()
      } catch (err) {
        console.error("Fehler beim Laden der Kategorien:", err)
      }
    },

    async loadOrders() {
      this.loadingOrders = true
      try {
        this.orders = await api.marketingStock.fetchOrders()
      } catch (err) {
        this.$toast.error("Protokoll konnte nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingOrders = false
      }
    },

    // --- DIALOGS & ACTIONS ---
    openCreateProductDialog() {
      this.isEditMode = false
      this.trackStock = false
      this.enableNotification = false
      this.productFormModel = {
        _id: null,
        barcode: "",
        title: "",
        category: "Allgemein",
        stock: 0,
        notificationThreshold: 5,
        notificationEmail: "da@wellcomefit.ch",
        active: true,
      }
      this.productDialogOpen = true

      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
      })
    },

    openEditProductDialog(product) {
      this.isEditMode = true
      this.trackStock = product.stock !== null
      this.enableNotification = product.notificationThreshold !== null
      this.productFormModel = {
        _id: product._id,
        barcode: product.barcode,
        title: product.title,
        category: product.category || "Allgemein",
        stock: product.stock !== null ? product.stock : 0,
        notificationThreshold: product.notificationThreshold !== null ? product.notificationThreshold : 5,
        notificationEmail: product.notificationEmail || "da@wellcomefit.ch",
        active: product.active,
      }
      this.productDialogOpen = true

      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.validate()
      })
    },

    closeProductDialog() {
      this.productDialogOpen = false
    },

    async saveProduct() {
      if (!this.$refs.productForm.validate()) return

      this.savingProduct = true
      try {
        const payload = {
          barcode: this.productFormModel.barcode,
          title: this.productFormModel.title,
          category:
            typeof this.productFormModel.category === "object" && this.productFormModel.category !== null
              ? this.productFormModel.category.text || this.productFormModel.category.value
              : this.productFormModel.category,
          stock: this.trackStock ? Number(this.productFormModel.stock) : null,
          notificationThreshold: this.trackStock && this.enableNotification ? Number(this.productFormModel.notificationThreshold) : null,
          notificationEmail: this.trackStock && this.enableNotification ? this.productFormModel.notificationEmail : "da@wellcomefit.ch",
          active: this.productFormModel.active,
        }

        if (this.isEditMode) {
          await api.marketingStock.updateProduct(this.productFormModel._id, payload)
          this.$toast.success("Produkt erfolgreich aktualisiert")
        } else {
          await api.marketingStock.createProduct(payload)
          this.$toast.success("Produkt erfolgreich erstellt")
        }

        this.closeProductDialog()
        this.loadProducts()
        this.loadCategories()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Speichern des Produkts"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.savingProduct = false
      }
    },

    confirmDeleteProduct(product) {
      this.productToDelete = product
      this.deleteDialogOpen = true
    },

    closeDeleteDialog() {
      this.deleteDialogOpen = false
      this.productToDelete = null
    },

    async executeDeleteProduct() {
      if (!this.productToDelete) return

      this.deletingProduct = true
      try {
        await api.marketingStock.deleteProduct(this.productToDelete._id)
        this.$toast.success("Produkt erfolgreich deaktiviert")
        this.closeDeleteDialog()
        this.loadProducts()
      } catch (err) {
        this.$toast.error("Fehler beim Deaktivieren des Produkts")
        console.error(err)
      } finally {
        this.deletingProduct = false
      }
    },

    // --- HELPER METHODS ---
    generateRandomBarcode() {
      // Generate a random 12-digit number + EAN calculation or just 13-digit random numeric string for convenience
      let code = "20" // prefix for internal use
      for (let i = 0; i < 11; i++) {
        code += Math.floor(Math.random() * 10)
      }
      this.productFormModel.barcode = code
    },

    getStockColor(stock) {
      if (stock <= 0) return "error"
      if (stock < 5) return "warning"
      return "success"
    },

    formatDateTime(d) {
      return d ? moment(d).format("DD.MM.YYYY HH:mm") : ""
    },

    truncateText(text, length) {
      if (!text) return ""
      if (text.length <= length) return text
      return text.substring(0, length) + "..."
    },

    // --- BARCODE SCANNING FOR ADMIN FORM ---
    triggerAdminCaptureFlow() {
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        window.flutter_inappwebview
          .callHandler("openCameraNative")
          .then((result) => {
            if (result && result.success) {
              const file = this.dataURLtoFile(result.base64, result.name || "barcode_capture.jpg")
              this.processAdminBarcodeFromImage(file)
            }
          })
          .catch(() => {
            this.$refs.adminFileInput.click()
          })
      } else {
        this.$refs.adminFileInput.click()
      }
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(",")
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },

    onAdminFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processAdminBarcodeFromImage(file)
      }
      event.target.value = ""
    },

    async processAdminBarcodeFromImage(file) {
      this.scanningFile = true
      try {
        await this.loadHtml5Qrcode()

        const scanDiv = document.createElement("div")
        scanDiv.id = "temp-admin-qr-reader"
        scanDiv.style.display = "none"
        document.body.appendChild(scanDiv)

        const html5QrCode = new window.Html5Qrcode("temp-admin-qr-reader")

        try {
          const decodedText = await html5QrCode.scanFile(file, true)
          this.productFormModel.barcode = decodedText.trim()
          this.$toast.success("Barcode erfolgreich gescannt")
        } catch (scanErr) {
          this.$toast.error("Kein Barcode auf dem Bild erkannt. Bitte deutlicher fotografieren.")
          console.log("Scan failure from file:", scanErr)
        } finally {
          html5QrCode.clear()
          document.body.removeChild(scanDiv)
        }
      } catch (err) {
        this.$toast.error("Scanner-Bibliothek konnte nicht initialisiert werden")
        console.error(err)
      } finally {
        this.scanningFile = false
      }
    },

    loadHtml5Qrcode() {
      return new Promise((resolve, reject) => {
        if (window.Html5Qrcode) {
          resolve()
          return
        }
        const script = document.createElement("script")
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js"
        script.type = "text/javascript"
        script.onload = () => resolve()
        script.onerror = (err) => reject(err)
        document.head.appendChild(script)
      })
    },

    // --- EDIT & CANCEL ORDERS (ADMIN) ---
    openEditOrderDialog(order) {
      this.orderToEdit = order
      this.orderFormModel.quantity = order.quantity
      this.editOrderDialogOpen = true
    },

    closeEditOrderDialog() {
      this.editOrderDialogOpen = false
      this.orderToEdit = null
    },

    async saveOrderEdit() {
      if (!this.orderToEdit) return

      this.savingOrderEdit = true
      try {
        await api.marketingStock.updateOrder(this.orderToEdit._id, { quantity: Number(this.orderFormModel.quantity) })
        this.$toast.success("Buchung erfolgreich aktualisiert")
        this.closeEditOrderDialog()
        await this.loadOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Bearbeiten der Buchung"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.savingOrderEdit = false
      }
    },

    confirmCancelOrder(order) {
      this.orderToCancel = order
      this.cancelOrderDialogOpen = true
    },

    closeCancelOrderDialog() {
      this.cancelOrderDialogOpen = false
      this.orderToCancel = null
    },

    async executeCancelOrder() {
      if (!this.orderToCancel) return

      this.cancelingOrder = true
      try {
        await api.marketingStock.deleteOrder(this.orderToCancel._id)
        this.$toast.success("Buchung erfolgreich storniert")
        this.closeCancelOrderDialog()
        await this.loadOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Stornieren der Buchung"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.cancelingOrder = false
      }
    },
  },
}
</script>

<style scoped>
.marketing-stock-admin-root {
  background-color: #0d0d0d;
  color: #fff;
}

.header-banner {
  background: linear-gradient(135deg, #1a1a1d, #16161a);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
}

.header-banner-glow {
  position: absolute;
  top: -50px;
  right: -50px;
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, transparent 70%);
  filter: blur(20px);
  pointer-events: none;
}

.header-title {
  letter-spacing: -0.5px;
}

.letter-spaced {
  letter-spacing: 1.5px;
}

.muted-text {
  color: rgba(255, 255, 255, 0.5);
}

.products-nav-btn {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0;
  transition: all 0.3s;
}

.products-nav-btn:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.tabs-nav >>> .v-tabs-bar {
  background-color: transparent !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.table-card {
  background: rgba(18, 18, 18, 0.6) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.transparent-table {
  background: transparent !important;
}

.transparent-table >>> th {
  font-weight: bold !important;
  color: rgba(255, 255, 255, 0.6) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.transparent-table >>> td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.font-mono {
  font-family: monospace, Courier, monospace;
}

.text-xs {
  font-size: 11px;
}

.filter-bar {
  border: 1px solid rgba(255, 255, 255, 0.03);
}

.search-field >>> .v-input__slot {
  transition: background 0.2s;
}

.search-field:hover >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.07) !important;
}

.border-glass {
  border: 1px solid rgba(255, 255, 255, 0.07) !important;
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.5) !important;
}
</style>
